import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import classNames from "classnames";
import { ecollatHelper, svgIcon } from "../../Functions";
import { HOST, HeaderPosition, HeaderOffset, iconconfig } from "../../config";
import { GlobalSearchContext } from "../../Components/Viewer/context";
import { generatePDFFromImages } from "../../Functions/images";
import Service from "../../Action/service";
import { ClickOnCustomMenuAnalatics, getDownloadTimeApi } from "../../Action";
import { ContactUsAnalatics, PdfDownloadAnalatics, PdfShareAnalatics } from "../../Functions/analytic";
import { GetNavigations } from "../../Functions/functions";
import useIsMobile from "../../Functions/use-is-mobile";
import "./header-v2.css";

const SEARCH_DELAY = 300;
const SEARCH_MIN_CHARS = 3;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.2;
const ZOOM_DEFAULT = 1.5;

const clampZoom = (value) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 100) / 100));
const PLACEMENTS = ["top", "right", "bottom", "left"];

const toCssSize = (value) => {
  if (value === undefined || value === null || value === "") return "0px";
  return typeof value === "number" ? `${value}px` : value;
};

const getFullscreenApi = () => {
  const doc = document;
  const el = doc.documentElement;
  return {
    request: el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen,
    exit: doc.exitFullscreen || doc.webkitExitFullscreen || doc.mozCancelFullScreen,
    current: () => doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement,
  };
};

const IconButton = ({ icon, title, onClick, active, id, className, children }) => (
  <button
    type="button"
    id={id}
    title={title}
    aria-label={title}
    className={classNames("hv2-btn", className, { "is-active": active })}
    onClick={onClick}
  >
    {svgIcon(icon)}
    {children}
  </button>
);

const HeaderV2 = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const match = useParams();
  const isMobile = useIsMobile();
  const { isShowCartIcon } = useContext(GlobalSearchContext);
  const { pdf, config = {} } = props;
  const { verticalscroll } = config;

  const [searchValue, setSearchValue] = useState("");
  const [netConn, setNetConn] = useState(navigator.onLine);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showIcons, setShowIcons] = useState({});
  const searchInputRef = useRef();
  const searchTimerRef = useRef();
  const lastSearchRef = useRef("");

  const pdfHelper = useMemo(() => (pdf ? new ecollatHelper(pdf) : null), [pdf]);

  const requestedPlacement = props.position || HeaderPosition;
  let placement = PLACEMENTS.includes(requestedPlacement) ? requestedPlacement : "top";
  if (isMobile && (placement === "left" || placement === "right")) placement = "top";
  const isVertical = placement === "left" || placement === "right";
  const offset = { ...HeaderOffset, ...props.offset };

  useEffect(() => {
    const fetchEnableOrNot = async () => {
      try {
        const result = await Service.get(`public/getenableornot/${window.__E_TT_Client.companySlug}`);
        if (result?.data?.success) setShowIcons(result.data.data);
      } catch (error) { }
    };
    fetchEnableOrNot();
  }, []);

  useEffect(() => {
    const updateConnection = () => {
      setNetConn(navigator.onLine);
      localStorage.setItem("isoffline", !navigator.onLine);
    };
    updateConnection();
    window.addEventListener("online", updateConnection);
    window.addEventListener("offline", updateConnection);
    return () => {
      window.removeEventListener("online", updateConnection);
      window.removeEventListener("offline", updateConnection);
    };
  }, []);

  useEffect(() => {
    if (props.isSearchOpen && searchInputRef.current) searchInputRef.current.focus();
    if (!props.isSearchOpen) {
      clearTimeout(searchTimerRef.current);
      lastSearchRef.current = "";
      setSearchValue("");
    }
  }, [props.isSearchOpen]);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const enablePopup = (type, data = {}, extra = {}) => {
    props.togglePopup({ isActive: true, data, type, ...extra });
  };

  const stopNavigation = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  const handleRemoveisLowrise = () => {
    localStorage.removeItem("isLowrise");
    localStorage.removeItem("pdf");
  };

  const handleCartToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    props.toggleCart();
    if (props.toggleSidebar) props.toggleSidebar(false);
  };

  const runSearch = (value) => {
    clearTimeout(searchTimerRef.current);
    const term = value.trim();
    if (term.length < SEARCH_MIN_CHARS || term === lastSearchRef.current) return;
    lastSearchRef.current = term;
    props.search(term);
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    clearTimeout(searchTimerRef.current);
    if (value.trim().length < SEARCH_MIN_CHARS) lastSearchRef.current = "";
    searchTimerRef.current = setTimeout(() => runSearch(value), SEARCH_DELAY);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    runSearch(searchValue);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const openSearch = () => {
    if (props.toggleSidebar) props.toggleSidebar(false);
    props.toggleSearch(true);
  };

  const closeSearch = () => {
    clearTimeout(searchTimerRef.current);
    lastSearchRef.current = "";
    setSearchValue("");
    props.toggleSearch(false, true);
    setTimeout(() => {
      const searchBtn = document.getElementById("idico-search");
      if (searchBtn) searchBtn.focus({ preventScroll: true });
    }, 0);
  };

  const handleMenuToggle = () => {
    const { toggleSidebar, isSidebarOpen } = props;
    toggleSidebar();
    if (!isSidebarOpen && pdf) {
      ClickOnCustomMenuAnalatics({
        companySlug: window.__E_TT_Client.companySlug,
        pdfID: pdf.pdfID,
      });
    }
  };

  const handleShare = async () => {
    enablePopup("share");
    PdfShareAnalatics({
      companySlug: window.__E_TT_Client.companySlug,
      pdfID: pdf?.pdfID,
    });
    await Service.post(`public/shareclick`, {
      companySlug: window.__E_TT_Client.companySlug,
      pdfID: pdf?.pdfID,
      isHome: props.isHome,
    });
  };

  const handleContact = async () => {
    enablePopup("contact", pdf?.contactInfo, { socialMediaLinks: props.socialMediaLinks });
    ContactUsAnalatics({ companySlug: window.__E_TT_Client.companySlug });
    await Service.post(`public/clickoncontactus`, {
      companySlug: window.__E_TT_Client.companySlug,
    });
  };

  const handleGoTo = (e) => {
    e.nativeEvent.stopImmediatePropagation();
    if (!pdf) return;
    const { com_slug, isPublish } = props;
    const { totalPages, pdfSlug } = pdf;

    if (e.key === "Enter") {
      const pageNo = Number(e.target.value);
      if (!pageNo || pageNo > totalPages || pageNo < 1) return;
      const base = isPublish && com_slug ? `/${com_slug}/${pdfSlug}` : `/${pdfSlug}`;
      navigate(`${base}/page/${pageNo}${verticalscroll ? "?scroll=true" : ""}`);
      e.target.value = "";
      return;
    }

    if (!/^[0-9]$/.test(e.key)) e.preventDefault();
  };

  const getPageLabel = () => {
    if (!pdf || !pdfHelper) return "";
    const total = pdf.totalPages;
    const pageNo = pdfHelper.sanitizePageNo(match.pageNo) || 1;

    if (pdfHelper.spreadCount() === 1 || pageNo === 1) return `${pageNo}`;
    if (total % 2 === 0 && pageNo >= total) return `${pageNo}`;
    return `${pageNo}-${pageNo + 1}`;
  };

  const downloadPDF = async () => {
    const { pdfSlug, pdfID, parent, pages, showForm, clientID } = pdf;
    const url = parent
      ? `${HOST}/viewsofpdf?id=${pdfID}&timestamp=${pdfSlug}`
      : `${HOST}/pdfdownload/${pdfID}`;

    if (pages) {
      setDownloadLoading(true);
      await generatePDFFromImages(pdfID, pages, pdf);
      setDownloadLoading(false);
      return;
    }

    const data = { url, pdfSlug, pdfID, isHeader: true, clientID };
    const popupConfig = { isActive: true, data, type: "downloadPDF" };

    if (showForm) {
      const { formID, isformBuilder } = pdf;
      if (isformBuilder && formID && formID !== "") {
        enablePopup("downloadPDFCustomForm", { ...popupConfig, formID });
      } else {
        enablePopup("downloadPDF", popupConfig);
      }
      return;
    }

    setDownloadLoading(true);
    PdfDownloadAnalatics({
      companySlug: window.__E_TT_Client.companySlug,
      pdfID,
      isHeader: true,
      isForm: false,
    });

    try {
      const res = await Service.get(url);
      const fileUrl = res.data.data;
      const fileName = res.headers["content-language"];
      let downloadStartTime;

      const response = await axios.get(fileUrl, {
        responseType: "blob",
        onDownloadProgress: () => {
          if (!downloadStartTime) downloadStartTime = new Date();
        },
      });

      getDownloadTimeApi({
        pdfID,
        clientID,
        downTime: new Date() - downloadStartTime,
      });

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const fullscreenApi = getFullscreenApi();
  const toggleFullScreen = () => {
    if (fullscreenApi.current()) {
      if (fullscreenApi.exit) fullscreenApi.exit.call(document);
    } else if (fullscreenApi.request) {
      fullscreenApi.request.call(document.documentElement);
    }
  };

  const hasMenu = () => {
    if (pdf?.parent) return false;
    if (props.cookiePolicyData) return !props.isHomePage;
    return !!(props.menuData && props.menuData.length > 0);
  };

  const renderLogo = () => {
    const clientInfo = window.__E_TT_Client;
    const logo = <img src={clientInfo.logo} alt={clientInfo.title} />;
    return (
      <div className="hv2-logo" onClick={handleRemoveisLowrise} onKeyDown={(e) => e.key === "Enter" && handleRemoveisLowrise()}>
        {clientInfo.isHome ? logo : <Link to={GetNavigations("/")}>{logo}</Link>}
      </div>
    );
  };

  const renderSearch = () => (
    <form
      className={classNames("hv2-search", { "is-open": props.isSearchOpen })}
      autoCapitalize="off"
      autoComplete="off"
      spellCheck="false"
      onSubmit={handleSearch}
    >
      <IconButton
        icon="ico-v2-search"
        title="Search"
        id="idico-search"
        onClick={props.isSearchOpen ? handleSearch : openSearch}
      />
      <input
        type="search"
        placeholder={`Type ${SEARCH_MIN_CHARS} or more characters to search`}
        ref={searchInputRef}
        value={searchValue}
        onChange={handleSearchChange}
        onKeyUp={stopNavigation}
        onFocus={() => props.toggleResult && props.toggleResult(true)}
        tabIndex={props.isSearchOpen ? 0 : -1}
      />
      <IconButton
        icon="ico-v2-close"
        title="Close search"
        className={classNames("hv2-search-close", { "is-loading": props.searchLoading })}
        onClick={closeSearch}
      />
    </form>
  );

  const renderCart = () => (
    <IconButton icon="ico-v2-cart" title="Cart" onClick={handleCartToggle}>
      {props.cartCounter > 0 && <span className="hv2-badge">{props.cartCounter}</span>}
    </IconButton>
  );

  const renderMenu = () => (
    <IconButton
      icon={props.isSidebarOpen ? "ico-v2-close" : "ico-v2-menu"}
      title="Menu"
      active={props.isSidebarOpen}
      onClick={handleMenuToggle}
    />
  );

  const renderDownload = () =>
    downloadLoading ? (
      <span className="hv2-btn hv2-loading" aria-label="Downloading">
        <span className="hv2-spinner" />
      </span>
    ) : (
      <IconButton
        icon="ico-v2-download"
        title="Download PDF"
        id={pdf?.showForm ? undefined : "idico-download"}
        onClick={downloadPDF}
      />
    );

  const renderPageCounter = () => {
    if (!pdf) return null;
    const label = getPageLabel();
    return (
      <div className="hv2-counter">
        <input
          type="tel"
          aria-label="Go to page"
          placeholder={label}
          size={Math.max(label.length, 2)}
          onKeyPress={handleGoTo}
          onKeyUp={stopNavigation}
        />
        <span>of {pdf.totalPages}</span>
      </div>
    );
  };

  const hasZoomBar = !!(props.setZoom && props.setZoomActive);

  const openZoom = () => {
    if (hasZoomBar) props.setZoom(ZOOM_DEFAULT);
    props.setZoomActive(true);
  };

  const closeZoom = () => {
    props.setZoomActive(false);
    props.setZoom(ZOOM_DEFAULT);
  };

  const changeZoom = (value) => props.setZoom(clampZoom(value));

  const renderZoomBar = () => {
    const zoom = clampZoom(props.zoom ?? ZOOM_DEFAULT);
    return (
      <div className="hv2-zoom">
        <IconButton
          icon="ico-v2-zoom-out"
          title="Zoom out"
          className="hv2-btn-plain"
          onClick={() => changeZoom(zoom - ZOOM_STEP)}
        />
        <input
          type="range"
          className="hv2-zoom-range"
          aria-label="Zoom level"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step="0.1"
          value={zoom}
          style={{ "--hv2-zoom-progress": `${((zoom - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN)) * 100}%` }}
          onChange={(e) => changeZoom(Number(e.target.value))}
          onKeyUp={stopNavigation}
        />
        <IconButton
          icon="ico-v2-zoom-in"
          title="Zoom in"
          className="hv2-btn-plain"
          onClick={() => changeZoom(zoom + ZOOM_STEP)}
        />
        <IconButton
          icon="ico-v2-cancel"
          title="Close zoom"
          className="hv2-btn-plain hv2-zoom-close"
          onClick={closeZoom}
        />
      </div>
    );
  };

  const renderShell = (start, center, end, className) => (
    <header
      className={classNames("header-v2", `header-v2--${placement}`, className, {
        "is-vertical": isVertical,
        "is-mobile": isMobile,
        "hv2-scroll": verticalscroll,
      })}
      style={{
        "--hv2-icon-color": iconconfig.color,
        "--hv2-top": toCssSize(offset.top),
        "--hv2-right": toCssSize(offset.right),
        "--hv2-bottom": toCssSize(offset.bottom),
        "--hv2-left": toCssSize(offset.left),
      }}
    >
      <div className="hv2-start">{start}</div>
      {center && <div className="hv2-center">{center}</div>}
      <div className="hv2-end">{end}</div>
    </header>
  );

  const renderSliderHeader = () => {
    const hasSearch = pdf?.search && !props.Generatedpdf;
    const showCart = netConn && isShowCartIcon;
    const showCatalog =
      props.togglePdfGenerate &&
      !props.hideCustomPdf &&
      !props.Generatedpdf &&
      !location.pathname.includes("custom");

    const showZoomBar = !isMobile && hasZoomBar && props.zoomActive;

    const center = showZoomBar ? (
      renderZoomBar()
    ) : (
      <>
        {renderPageCounter()}
        {!isMobile && (
          <div className="hv2-group">
            {props.setZoomActive && (
              <IconButton
                icon="ico-v2-zoom-in"
                title="Zoom"
                active={props.zoomActive}
                onClick={props.zoomActive ? () => props.setZoomActive(false) : openZoom}
              />
            )}
            {pdf?.lowrise && renderDownload()}
          </div>
        )}
      </>
    );

    const end = isMobile ? (
      <>
        {netConn && hasSearch && renderSearch()}
        {showCart && renderCart()}
        {netConn && hasMenu() && renderMenu()}
      </>
    ) : (
      <>
        {netConn && hasSearch && renderSearch()}
        {showCatalog && (
          <IconButton
            icon="ico-v2-catalog"
            title="Create your own catalog"
            id="idico-pdfGenerate"
            onClick={props.togglePdfGenerate}
          />
        )}
        {fullscreenApi.request && (
          <IconButton icon="ico-v2-fullscreen" title="Fullscreen" onClick={toggleFullScreen} />
        )}
        {showIcons?.showShare && (
          <IconButton icon="ico-v2-share" title="Share" id="idico-share" onClick={handleShare} />
        )}
        {showIcons?.showContact && (
          <IconButton icon="ico-v2-contact" title="Contact" id="idico-contact" onClick={handleContact} />
        )}
        {showCart && renderCart()}
        {netConn && hasMenu() && renderMenu()}
      </>
    );

    return renderShell(renderLogo(), center, end);
  };

  const renderPdfGenerateHeader = () =>
    renderShell(
      renderLogo(),
      null,
      <div className="hv2-group">
        <span className="hv2-heading-label">Create your own catalog</span>
        <IconButton icon="ico-v2-close" title="Close" onClick={props.togglePdfGenerate} />
      </div>
    );

  const renderDefaultHeader = () =>
    renderShell(
      renderLogo(),
      null,
      <>
        {props.hasSearch && netConn && !props.Generatedpdf && renderSearch()}
        {hasMenu() && renderMenu()}
      </>
    );

  if (props.embededlink) return null;

  switch (props.headerType) {
    case "slider":
      return renderSliderHeader();
    case "generate-pdf":
      return renderPdfGenerateHeader();
    default:
      return renderDefaultHeader();
  }
};

export default HeaderV2;
